import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-goutte-sm text-sm font-semibold cursor-pointer transition-all duration-200 ease-in-out disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[#30A6DC]/30",
  {
    variants: {
      variant: {
        default: "bg-ink text-white hover:bg-[#0B2A50] shadow-sm",
        destructive:
          "bg-[#B42318] text-white hover:bg-[#912018] focus-visible:ring-[#B42318]/20",
        outline:
          "border border-[#D5DCE6] bg-white text-ink shadow-xs hover:bg-ground hover:text-ink",
        secondary:
          "bg-[#EEF1F6] text-ink hover:bg-[#E3E8F0]",
        ghost:
          "hover:bg-ground hover:text-ink",
        link: "text-brand-deep underline-offset-4 hover:underline",
        custom: "",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
